import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import {
  FilterSortWidgetData,
  SelectionType,
  SubcollectionOption,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'
import React, { useCallback, useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { useQuinceRouter } from '@src/modules/navigation'
import { useCollectionFeature } from '@src/pages/[...slug]/components/collection/collection-feature-provider'
import { ALL_SUBCOLLECTION_FILTER_ID } from '@src/pages/[...slug]/components/collection/collection.constants'
import { scrollToProductSection } from '@src/pages/[...slug]/components/collection/collection.helpers'
import collectionStyles from '@src/pages/[...slug]/components/collection/collectionPage.module.scss'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import {
  Filter,
  FilterConfig,
  FilterOptions,
  SelectedFilter,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import CollectionFilters from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilters'
import { SubCollectionCardList } from '@src/pages/[...slug]/components/collection/molecules/subCollectionCardList'
import {
  buildSubcollectionNavigationSearch,
  buildSubcollectionPath,
  normalizeCollectionPath,
  setPendingCollectionProductScroll,
} from '@src/pages/[...slug]/components/collection/utils'
import { useFilterSortContext } from '@src/pages/clp1/contexts/filter-sort-context'
import { useFilterUpdateQuery } from '@src/pages/clp1/hooks/use-filter-update-query'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import plStyles from '../pl-page-renderer/pl-layout.module.scss'

const ALL_SUBCOLLECTION_IDS = [ALL_SUBCOLLECTION_FILTER_ID, 'All', 'all'] as const
const DEFAULT_SUBCOLLECTION_ID = 'subcollections'
const FILTER_UI_TYPE_CARD = 'card'
const FILTER_UI_TYPE_CHECKBOX = 'checkbox'
type SubcollectionOptionWithSlug = SubcollectionOption & { slug?: string | null }

function isAllSubcollection(id: string | undefined | null): boolean {
  if (typeof id !== 'string') {
    return false
  }
  return ALL_SUBCOLLECTION_IDS.some(val => val.toLowerCase() === id.toLowerCase())
}

function normalizeSubcollectionValue(value: string): string {
  return value.trim().toLowerCase()
}

function getSubcollectionPath({
  collectionPath,
  option,
}: {
  collectionPath: string
  option: SubcollectionOptionWithSlug
}): string {
  if (isAllSubcollection(option.id) || isAllSubcollection(option.slug)) {
    return collectionPath
  }

  return buildSubcollectionPath({
    collectionSlug: collectionPath,
    subcollectionSlug: option.slug,
  })
}

const FilterAndSortWidgetComponent = (props: PLTypedWidgetProps<FilterSortWidgetData>) => {
  const collectionFeature = useCollectionFeature()
  const pageMeta = usePLPageMeta()
  const router = useQuinceRouter()
  const data = props.data.filterSortData
  const filtersData = data?.filters
  const subcollectionsData = data?.subcollections
  const subcollectionId = subcollectionsData?.id ?? DEFAULT_SUBCOLLECTION_ID
  const { deviceType } = useDeviceInfo()

  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const collectionPath = normalizeCollectionPath(pageMeta.pageMetaData?.slug ?? pageMeta.pathname)
  const shouldUsePathSubcollectionNavigation = collectionFeature.subCollectionUrlPlacement === 'path'

  const {
    selectedFilters: urlSelectedFilters,
    sortBy: urlSortBy,
    updateFilter,
    updateSort,
    clearAllFilters,
    setOnlyFilter,
    setFilters,
  } = useFilterSortContext()

  const sortOptions = useMemo(
    () => data?.sorting?.options.map(opt => ({ value: opt.id, label: opt.displayName })),
    [data?.sorting?.options],
  )
  const sortBy = urlSortBy || (data?.sorting?.options.find(option => option.selected)?.id ?? SORT_BY.FEATURED.value)

  const pathSelectedSubCollectionIds = useMemo(() => {
    if (!shouldUsePathSubcollectionNavigation) {
      return new Set<string>()
    }

    const currentPath = normalizeCollectionPath(pageMeta.pathname)
    const matchedOption = subcollectionsData?.options.find(
      option => getSubcollectionPath({ collectionPath, option }) === currentPath,
    )

    return matchedOption ? new Set([matchedOption.id]) : new Set<string>()
  }, [collectionPath, pageMeta.pathname, shouldUsePathSubcollectionNavigation, subcollectionsData?.options])

  // Path-mode subcoll nav: changing subcollections resets filter/sort state, but keeps rollout/marketing params.
  const buildSubcollectionNavigationUrl = useCallback(
    (target: string): string =>
      typeof window === 'undefined' ? target : `${target}${buildSubcollectionNavigationSearch(window.location.search)}`,
    [],
  )

  const updateQuery = useFilterUpdateQuery({
    updateFilter,
    updateSort,
    clearAllFilters,
    setOnlyFilter,
    setFilters,
    subcollectionId,
    isDesktop,
    // Lets the hook detect "click already-selected" in query mode and clear instead of re-setting.
    selectedFilters: urlSelectedFilters,
    // Path mode: navigate to the subcoll URL, or back to parent if the path itself is already active.
    // Legacy query landings can look selected, but their next interaction should still move to the path URL.
    onSelectSubcollection: shouldUsePathSubcollectionNavigation
      ? value => {
          const selectedOption = subcollectionsData?.options.find(option => option.id === value)
          if (!selectedOption) return
          if (pathSelectedSubCollectionIds.has(value)) {
            router.push(buildSubcollectionNavigationUrl(collectionPath), { scroll: false })
            return
          }
          const targetPath = getSubcollectionPath({ collectionPath, option: selectedOption })
          router.push(buildSubcollectionNavigationUrl(targetPath), { scroll: false })
        }
      : undefined,
    onResetSubcollection: shouldUsePathSubcollectionNavigation
      ? () => {
          router.push(buildSubcollectionNavigationUrl(collectionPath), { scroll: false })
        }
      : undefined,
    resolveSubcollectionSelection: shouldUsePathSubcollectionNavigation
      ? filters => {
          if (!(subcollectionId in filters)) {
            return undefined
          }
          return filters[subcollectionId]?.[0] ?? null
        }
      : undefined,
  })
  const filterOptions = filtersData?.options ?? []
  const initialFilters: Filter[] = filterOptions
    .filter(option => option.options.length > 0)
    .map(option => {
      const isSingleSelect = option.selectionType === SelectionType.SINGLE_SELECT

      return new Filter({
        name: option.id,
        values: option.options.map(
          filterOptionItem =>
            new FilterOptions({
              name: filterOptionItem.id,
              value: filterOptionItem.id,
              displayName: filterOptionItem.displayName,
              hex: filterOptionItem.color?.hex ?? '',
              imgURL: filterOptionItem.image?.url ?? '',
              displayType: ProductOptionDisplayType.COLOR,
            }),
        ),
        config: new FilterConfig({
          columnsToShow: 2,
          label: option.displayName,
          type: isSingleSelect ? FILTER_UI_TYPE_CARD : FILTER_UI_TYPE_CHECKBOX,
        }),
      })
    })

  const selectedSubCollectionIds = useMemo(() => {
    // In path mode, use path-derived selection only when a non-All subcollection is active.
    // When the path matches only the base-collection "All" slot, fall through to read the
    // URL filter so query-carried subcollection params highlight the correct card.
    if (shouldUsePathSubcollectionNavigation && pathSelectedSubCollectionIds.size > 0) {
      const hasActiveSubcollection = Array.from(pathSelectedSubCollectionIds).some(id => !isAllSubcollection(id))
      if (hasActiveSubcollection) return pathSelectedSubCollectionIds
    }

    const rawValues = urlSelectedFilters[subcollectionId] ?? []
    if (rawValues.length === 0) return new Set<string>()

    // Dual-match: id OR displayName (normalized) handles both slug-form and
    // title-form query values highlighting the correct card.
    const normalizedUrlValues = new Set(rawValues.map(normalizeSubcollectionValue))
    return new Set(
      (subcollectionsData?.options ?? [])
        .filter(
          option =>
            normalizedUrlValues.has(normalizeSubcollectionValue(option.id)) ||
            normalizedUrlValues.has(normalizeSubcollectionValue(option.displayName)),
        )
        .map(option => option.id),
    )
  }, [
    pathSelectedSubCollectionIds,
    shouldUsePathSubcollectionNavigation,
    subcollectionId,
    subcollectionsData?.options,
    urlSelectedFilters,
  ])

  const selectedFiltersObj = useMemo(() => {
    const selectedFilters = { ...urlSelectedFilters } as SelectedFilter
    if (shouldUsePathSubcollectionNavigation && pathSelectedSubCollectionIds.size > 0) {
      const hasActiveSubcollection = Array.from(pathSelectedSubCollectionIds).some(id => !isAllSubcollection(id))
      if (hasActiveSubcollection) {
        selectedFilters[subcollectionId] = Array.from(pathSelectedSubCollectionIds)
      }
    }
    return selectedFilters
  }, [pathSelectedSubCollectionIds, shouldUsePathSubcollectionNavigation, subcollectionId, urlSelectedFilters])

  const hasAnySubcollectionSelected = useMemo(() => {
    if (selectedSubCollectionIds.size === 0) return false
    const selectedValues = Array.from(selectedSubCollectionIds)
    return selectedValues.some(value => !isAllSubcollection(value))
  }, [selectedSubCollectionIds])

  const subCollectionCards: SubCollection[] = useMemo(
    () =>
      subcollectionsData?.options.map(
        option =>
          new SubCollection({
            id: option.id,
            title: option.displayName,
            slug: shouldUsePathSubcollectionNavigation ? getSubcollectionPath({ collectionPath, option }) : '',
            seoTitle: option.displayName,
            subTitle: undefined,
            showAllColors: true,
            noIndex: false,
            productCards: undefined,
            marketingPanelBase: undefined,
            marketingPanelMobile: undefined,
            imageDesktop: new ImageDomain({
              alt: option.image?.alt ?? '',
              height: option.image?.height ?? 0,
              width: option.image?.width ?? 0,
              url: option.image?.url ?? '',
              id: '',
              fileName: '',
              displayModelInfo: false,
            }),
            additionalAttributes: undefined,
            navigationId: undefined,
            revSortedIdColor: undefined,
            totalProducts: undefined,
          }),
      ) ?? [],
    [collectionPath, shouldUsePathSubcollectionNavigation, subcollectionsData],
  )

  const onSubCollectionCardClick = useCallback(
    (subCollection: SubCollection) => {
      const subcollectionIdStr = subCollection.id.toString()

      trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_FILTER_USED, {
        filter_available: subCollectionCards.map(item => item.title),
        filter_used: subCollection.title,
      })

      if (!shouldUsePathSubcollectionNavigation) {
        if (isAllSubcollection(subcollectionIdStr)) {
          clearAllFilters({ preserveScrollPosition: true, trackFiltersCleared: false })
          scrollToProductSection(!isDesktop)
          return
        }

        const isSelected = selectedSubCollectionIds.has(subcollectionIdStr)

        if (isSelected) {
          clearAllFilters({ preserveScrollPosition: true, trackFiltersCleared: false })
        } else {
          setOnlyFilter(subcollectionId, [subcollectionIdStr], { preserveScrollPosition: true })
        }
        scrollToProductSection(!isDesktop)
        return
      }

      // Path mode toggle: click the active subcoll → back to parent. Uses selectedSubCollectionIds
      // (same set as card highlighting) so the decision survives any pathname-shape drift.
      const isCurrentlySelected =
        !isAllSubcollection(subcollectionIdStr) && selectedSubCollectionIds.has(subcollectionIdStr)
      if (isCurrentlySelected) {
        router.push(buildSubcollectionNavigationUrl(collectionPath), { scroll: false })
        return
      }

      const targetPath = isAllSubcollection(subcollectionIdStr)
        ? collectionPath
        : normalizeCollectionPath(subCollection.slug ?? collectionPath)

      if (pageMeta.pathname === targetPath) {
        // "All" on parent: nothing to navigate.
        return
      }

      if (!isAllSubcollection(subcollectionIdStr)) {
        setPendingCollectionProductScroll(targetPath)
      }
      // scroll: false avoids the jarring jump-to-top so post-nav scroll-to-products is smooth.
      router.push(buildSubcollectionNavigationUrl(targetPath), { scroll: false })
    },
    [
      buildSubcollectionNavigationUrl,
      clearAllFilters,
      collectionPath,
      isDesktop,
      pageMeta.pathname,
      router,
      selectedSubCollectionIds,
      setOnlyFilter,
      shouldUsePathSubcollectionNavigation,
      subcollectionId,
      subCollectionCards,
    ],
  )

  const hasSubCollectionCards = subCollectionCards.length > 0

  return (
    <>
      {hasSubCollectionCards && (
        <div className={collectionStyles.subCollectionCardList}>
          <SubCollectionCardList
            subCollections={subCollectionCards}
            isSubCollectionSelected={(sub: SubCollection) => {
              const subId = sub.id.toString()
              if (isAllSubcollection(subId)) {
                return !hasAnySubcollectionSelected
              }
              return selectedSubCollectionIds.has(subId)
            }}
            onCardClick={onSubCollectionCardClick}
            slug={shouldUsePathSubcollectionNavigation ? collectionPath : ''}
            collectionExploreEverythingImage={undefined}
            contentful_id={''}
          />
        </div>
      )}

      <div id="filter-section" className={!hasSubCollectionCards ? plStyles.filterSectionTopSpacing : ''}>
        <CollectionFilters
          totalProductsCount={data?.total ?? 0}
          initialFilters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFiltersObj}
          updateQuery={updateQuery}
          collectionPage={undefined}
          showLoader={false}
          subCollectionCards={subCollectionCards}
          sortOptions={sortOptions}
        />
      </div>
    </>
  )
}

export const FilterAndSortWidget: PLTypedWidgetInterface<FilterSortWidgetData, WidgetType.FILTER_AND_SORT> = {
  id: WidgetType.FILTER_AND_SORT,
  errorBoundary: PLBaseErrorBoundary,
  render: FilterAndSortWidgetComponent,
}
